import { Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LibraryCategoryDialogComponent } from '@components/editor/library/library-category-dialog/library-category-dialog.component';
import { LibraryDialogComponent } from '@components/editor/library/library-dialog/library-dialog.component';
import { LibraryService } from '@service/library.service';
import { ShipyardService } from '@service/shipyard.service';
import { Library, LibraryAndCategories } from 'icreate/common/models/library';
import { LibraryCategory } from 'icreate/common/models/library-category';
import { Vessel } from 'icreate/common/models/vessel';
import { lastValueFrom } from 'rxjs';
import { ButtonLabel, DuplicateItemType } from '@helpers/constants';
import { LibraryComponentService } from '@service/library-component.service';
import { CanvasService } from '@service/canvas/canvas.service';
import { Utils } from '@helpers/utils';
import { DuplicateMimicDialogComponent } from '@components/editor/duplicate-mimic-dialog/duplicate-mimic-dialog.component';

@Component({
    selector: 'app-library-tab',
    templateUrl: './library-tab.component.html',
    styleUrls: ['../editor-navigator.component.css', './library-tab.component.css'],
    standalone: false,
})
export class LibraryTabComponent {
    @Input() vessel: Vessel;
    @Input() libraryList: Library[] = [];
    @Input() canCreateOrDelete: boolean;
    @Input() showOptionsAll: boolean;
    @Input() showOption: Library;
    @Input() selectedCategory: LibraryCategory;

    @Output() onOpenCategoryOption = new EventEmitter<{
        category: LibraryCategory;
        event: MouseEvent;
    }>();
    @Output() onOpenComponentDialog = new EventEmitter<LibraryCategory>();
    @Output() onCloseComponentDialog = new EventEmitter<void>();
    @Output() onDeleteLibrary = new EventEmitter<Library>();
    @Output() onCloseCategoryOption = new EventEmitter<void>();
    @Output() onOpenExpanded = new EventEmitter<string>();

    @ViewChild('libraryListElement') libraryListElement: ElementRef;

    selectedLibrary: Library = null;

    dynamicHeight: number;
    elementHeight: number;

    defaultExpanded = false;

    readonly defaultItems = [
        { name: 'mimic.library.category.valves', svgIcon: 'valve' },
        { name: 'mimic.library.category.tanks', svgIcon: 'tank' },
        { name: 'mimic.library.category.pumps', svgIcon: 'control' },
        { name: 'mimic.library.category.fans', svgIcon: 'fan' },
        { name: 'mimic.library.category.switch', svgIcon: 'toggle-switch' },
        { name: 'mimic.library.category.gauge', svgIcon: 'gauge' },
    ];

    constructor(
        private shipyardService: ShipyardService,
        private dialog: MatDialog,
        private librarySerivce: LibraryService,
        private libraryComponentService: LibraryComponentService,
        private canvasService: CanvasService
    ) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (this.showOption) {
            this.showOption = null;
        }
        if (this.showOptionsAll) {
            this.showOptionsAll = false;
        }
    }

    openOrCloseCategory(libraryId: string, index: number, expanded: boolean) {
        if (!expanded) {
            this.selectedLibrary = { ...this.libraryList[index] };
            this.selectedCategory = undefined;
        } else {
            this.selectedLibrary = undefined;
            this.selectedCategory = undefined;
        }
        this.onOpenExpanded.emit(libraryId);
        this.onCloseComponentDialog.emit();
    }

    selectLibrary(library: Library) {
        this.selectedLibrary = library;
    }

    async createLibrary() {
        if (!this.canCreateOrDelete) return;
        try {
            const shipyardList = await this.shipyardService.getList();
            this.dialog.open(LibraryDialogComponent, {
                data: {
                    vessel: this.vessel,
                    libraryList: this.libraryList,
                    shipyardList,
                },
            });
        } catch (error) {
            console.log(error);
        }
    }

    createCategory(library: Library) {
        this.closeOptions();
        this.dialog.open(LibraryCategoryDialogComponent, {
            data: {
                libraryList: this.libraryList,
                vessel: this.vessel,
                library,
            },
        });
    }

    openLibraryOptions(library: Library, libraryIndex: number) {
        this.showOptionsAll = false;
        this.dynamicHeight = undefined;
        this.elementHeight = undefined;
        if (this.showOption?.id === library.id) {
            this.showOption = undefined;
            return;
        }
        const libraryElement =
            this.libraryListElement.nativeElement.getElementsByClassName('library-item')[libraryIndex];
        this.elementHeight = libraryElement?.getBoundingClientRect()?.top + 40;
        if (libraryElement && this.elementHeight) {
            this.dynamicHeight = this.elementHeight;
            this.showOption = library;
            this.onCloseCategoryOption.emit();
        }
    }

    openCategoryOptions(category: LibraryCategory, event: MouseEvent) {
        this.closeOptions();
        this.onOpenCategoryOption.emit({ category, event });
    }

    openComponentDialog(category: LibraryCategory) {
        this.selectedLibrary = undefined;
        this.onOpenComponentDialog.emit(category);
    }

    deleteLibrary(library: Library) {
        this.closeOptions();
        this.onDeleteLibrary.emit(library);
    }

    async editLibrary(library: Library) {
        if (!this.canCreateOrDelete) return;
        try {
            this.closeOptions();
            const shipyardList = await this.shipyardService.getList();
            this.dialog.open(LibraryDialogComponent, {
                data: {
                    vessel: this.vessel,
                    libraryList: this.libraryList,
                    selectedLibrary: library,
                    shipyardList,
                },
            });
        } catch (error) {
            console.log(error);
        }
    }

    async duplicateLibrary(library: Library & LibraryAndCategories) {
        try {
            const existingNames = this.libraryList.map(lib => lib.name);
            const dialogRef = this.dialog.open(DuplicateMimicDialogComponent, {
                data: {
                    title: 'mimic.duplicate.duplicate-component-library.title',
                    description: 'mimic.duplicate.duplicate-component-library.description',
                    initialName: library.name,
                    existingNames: existingNames,
                    type: DuplicateItemType.LIBRARY,
                    submitButtonLabel: ButtonLabel.DUPLICATE,
                    items: library.libraryCategories,
                    selectionLabel: 'mimic.duplicate.duplicate.item-label.category',
                    autoIncrementName: true,
                    showExtraOptions: true,
                },
            });

            const { name: newName, selectedOptions } = await lastValueFrom(dialogRef.afterClosed());
            if (!newName) return;

            this.closeOptions();

            const canvas = this.canvasService.getCanvas();
            const categoriesWithComponents = await Promise.all(
                (selectedOptions || []).map(async (category: any) => {
                    let components = category.componentList || [];
                    if (!components.length && !category.id.startsWith('default-')) {
                        components = await this.libraryComponentService.getByLibraryCategoryId(category.id);
                    }

                    const componentsWithIdMapping = Utils.mapItemsWithSvgIdMapping(components, 'svg', canvas);

                    return {
                        ...category,
                        componentList: componentsWithIdMapping,
                    };
                })
            );

            const libraryData = {
                ...library,
                name: newName,
                libraryCategories: categoriesWithComponents,
            };

            await this.librarySerivce.duplicateLibrary(libraryData);
        } catch (error) {
            console.log(error);
        }
    }

    toggleDefault() {
        if (!this.canCreateOrDelete) return;
        this.defaultExpanded = !this.defaultExpanded;
        this.onCloseComponentDialog.emit();
    }

    openDefaultComponentDialog(itemName: string) {
        const categoryId = itemName.split('.').pop() ?? itemName;
        const virtualCategory: LibraryCategory = {
            id: `default-${categoryId}`,
            libraryId: 'default',
            shipyardId: this.vessel?.shipyardId ?? '',
            name: itemName,
        };
        this.selectedLibrary = undefined;
        this.onOpenComponentDialog.emit(virtualCategory);
    }

    closeOptions() {
        this.showOption = null;
        this.showOptionsAll = false;
    }
}
